import AXIOS from "@/axios";
import { APIResponse } from "@/types";
import type {
  CreateEvalCategoryPayload,
  CreateEvalPayload,
  EvalCategoryListQuery,
  EvalListQuery,
  EvalRecordType,
} from "@/types/evaluation.model";
import { removeEmptyProps } from "@/utils/constants";

function withEvalType<T extends { type?: EvalRecordType }>(
  params: T
): T & { type: EvalRecordType } {
  return { ...params, type: params.type ?? "course" };
}

/**
 * Fetch evaluation categories for a course or class offering.
 * course_id = grade id when type=course, class id when type=class.
 */
export const fetchEvaluationCategories = async (
  query: EvalCategoryListQuery
): Promise<{
  data: APIResponse<unknown[]>;
}> => {
  return AXIOS.get<APIResponse<unknown[]>>("eval/api/cat/v1/list", {
    params: removeEmptyProps(withEvalType(query)),
  });
};

/**
 * Create an evaluation category for a course or class offering.
 */
export const createEvaluationCategory = async (
  payload: CreateEvalCategoryPayload
): Promise<{
  data: APIResponse<unknown>;
}> => {
  return AXIOS.post<APIResponse<unknown>>("eval/api/cat/v1/add", payload);
};

/**
 * Fetch evaluation list by resource id and student id.
 */
export const fetchEvaluationList = async (
  query: EvalListQuery
): Promise<{
  data: APIResponse<unknown>;
}> => {
  return AXIOS.get<APIResponse<unknown>>("eval/api/v1/list", {
    params: removeEmptyProps(withEvalType(query)),
  });
};

/**
 * Save evaluation scores and remarks for a student.
 */
export const createEvaluation = async (
  payload: CreateEvalPayload
): Promise<{
  data: APIResponse<unknown>;
}> => {
  return AXIOS.post<APIResponse<unknown>>("eval/api/v1/add", payload);
};
